import { safeLocalStorage } from "@/lib/safeStorage";
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import {
  CaretLeft,
  CaretRight,
  ArrowRight,
  ArrowsInSimple,
  Question,
} from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface TourStep {
  /** Unique identifier for the step */
  id: string;
  /** CSS selector for the element to highlight. A list is tried in order; the first one on the page wins. */
  target?: string | string[];
  /**
   * A step that speaks for itself: centred over a dimmed page like a dialog, pointing at
   * nothing. It opens on a deep-forest hero — icon, title and a line of copy — with
   * `render` (a checklist, say) on the stone body under it. Its footer offers Start
   * (carry on) and Later, which parks the tour in the corner when it can be set aside
   * and otherwise ends it.
   */
  modal?: boolean;
  /**
   * A clip that IS the modal step: it sits in the body with native controls.
   * Sound on, no autoplay, plays once. Served from the CDN, not the bundle.
   */
  media?: { src: string; poster?: string };
  /** Title of the step */
  title: string;
  /** What the thing is — one line. */
  content: string;
  /** What to press there, when the step has a thing to do. Set on its own line. */
  action?: string;
  /** Position of the tooltip relative to the target */
  placement?: "top" | "bottom" | "left" | "right";
  /** Optional icon to display */
  icon?: React.ReactNode;
  /** Optional extra content rendered under the text — a QR code, a link, a checklist. */
  render?: React.ReactNode;
  /**
   * For a step that introduces a whole page rather than one control: the handful of
   * things on it worth knowing, each pinned with a numbered chip on the page itself and
   * listed under the step's text. A callout whose target is not on the page is simply
   * left off the page; its line still reads.
   */
  callouts?: TourCallout[];
}

export interface TourCallout {
  /**
   * CSS selector for the thing on the page. A list is tried in order. Left out, the
   * line is listed in the card but pins nothing — for things too small or too close
   * together to carry their own chip.
   */
  target?: string | string[];
  /** One line on what it is. */
  label: string;
  /**
   * False for a target too small or too close to its neighbours to wear a number all
   * the time — it lights up only while its line is hovered. Default true.
   */
  pin?: boolean;
}

export interface FeatureTourProps {
  /** Unique key for storing tour completion in localStorage */
  tourKey: string;
  /** Array of tour steps */
  steps: TourStep[];
  /** Callback when tour completes */
  onComplete?: () => void;
  /** Callback when tour is skipped */
  onSkip?: () => void;
  /** Whether to show the tour (controlled mode) */
  isOpen?: boolean;
  /** Callback to control tour visibility */
  onOpenChange?: (open: boolean) => void;
  /** Whether to auto-start on first visit */
  autoStart?: boolean;
  /** Current step id while the tour is showing a step, or null when it is closed or set aside. */
  onStepId?: (id: string | null) => void;
  /**
   * Lets the reader step out to use the page underneath: "Try it yourself" (and, on the
   * opening dialog, clicking the dimmed page) shrinks the tour to a corner pill instead of
   * ending it; "Resume tour" comes back to the same step. A pointer step never covers
   * the page, so there is nothing to click "outside" of — the page is simply usable.
   */
  collapsible?: boolean;
}

interface TooltipPosition {
  top: number;
  left: number;
  /** Which edge the arrow sits on; "none" for a card docked in a corner, pointing at nothing. */
  arrowPosition: "top" | "bottom" | "left" | "right" | "none";
  /** Land on the spot rather than glide there — the first placement after having nowhere to be. */
  instant: boolean;
}

interface FoundTarget {
  element: Element;
  rect: DOMRect;
  /** Which of the step's selectors matched — 0 is the one the step would rather point at. */
  rank: number;
}

/**
 * One tour on screen at a time. A tour that wants to auto-start while another is open
 * waits for that one to close, so two spotlights never fight over the same page.
 */
let activeTourKey: string | null = null;
const TOUR_CLOSED_EVENT = "ruta:feature-tour-closed";

/**
 * Selectors are tried in order, so a step's real target beats its fallback (usually the
 * nav link) the moment it renders. An element that is in the page but not laid out — a
 * tab panel that is not showing, a nav hidden at this width — has no size, and pointing
 * at it would pin the spotlight to the top-left corner; those are passed over.
 */
function findTarget(target: string | string[]): FoundTarget | null {
  const selectors = Array.isArray(target) ? target : [target];
  for (const [rank, selector] of selectors.entries()) {
    for (const element of Array.from(document.querySelectorAll(selector))) {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return { element, rect, rank };
    }
  }
  return null;
}

function sameBox(a: DOMRect, b: DOMRect) {
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

function calculatePosition(
  targetRect: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number,
  placement: TourStep["placement"] = "bottom",
): Omit<TooltipPosition, "instant"> {
  const padding = 12;
  const arrowSize = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = 0;
  let left = 0;
  let arrowPosition: TooltipPosition["arrowPosition"] = "top";

  switch (placement) {
    case "top":
      top = targetRect.top - tooltipHeight - padding - arrowSize;
      left = targetRect.left + (targetRect.width - tooltipWidth) / 2;
      arrowPosition = "bottom";
      break;
    case "bottom":
      top = targetRect.bottom + padding + arrowSize;
      left = targetRect.left + (targetRect.width - tooltipWidth) / 2;
      arrowPosition = "top";
      break;
    case "left":
      top = targetRect.top + (targetRect.height - tooltipHeight) / 2;
      left = targetRect.left - tooltipWidth - padding - arrowSize;
      arrowPosition = "right";
      break;
    case "right":
      top = targetRect.top + (targetRect.height - tooltipHeight) / 2;
      left = targetRect.right + padding + arrowSize;
      arrowPosition = "left";
      break;
  }

  const clampX = (value: number) =>
    Math.min(Math.max(value, padding), Math.max(padding, viewportWidth - tooltipWidth - padding));
  const clampY = (value: number) =>
    Math.min(Math.max(value, padding), Math.max(padding, viewportHeight - tooltipHeight - padding));

  left = clampX(left);
  top = clampY(top);

  // Pushing the popover back inside the window can land it on the very thing it is
  // pointing at — covering the tabs and headings the step is talking about. When that
  // happens, take the nearest side with room for it, below first.
  const sits = (candidateTop: number, candidateLeft: number) =>
    !(
      candidateLeft + tooltipWidth <= targetRect.left - arrowSize ||
      candidateLeft >= targetRect.right + arrowSize ||
      candidateTop + tooltipHeight <= targetRect.top - arrowSize ||
      candidateTop >= targetRect.bottom + arrowSize
    );

  if (sits(top, left)) {
    const centredX = clampX(targetRect.left + (targetRect.width - tooltipWidth) / 2);
    const centredY = clampY(targetRect.top + (targetRect.height - tooltipHeight) / 2);
    const alternatives: Array<[number, number, TooltipPosition["arrowPosition"]]> = [
      [targetRect.bottom + padding + arrowSize, centredX, "top"],
      [targetRect.top - tooltipHeight - padding - arrowSize, centredX, "bottom"],
      [centredY, targetRect.right + padding + arrowSize, "left"],
      [centredY, targetRect.left - tooltipWidth - padding - arrowSize, "right"],
    ];
    const room = alternatives.find(
      ([candidateTop, candidateLeft]) =>
        candidateTop >= padding &&
        candidateTop + tooltipHeight <= viewportHeight - padding &&
        candidateLeft >= padding &&
        candidateLeft + tooltipWidth <= viewportWidth - padding &&
        !sits(candidateTop, candidateLeft),
    );
    if (room) {
      [top, left, arrowPosition] = room;
    }
  }

  return { top, left, arrowPosition };
}

type Edges = Pick<DOMRect, "left" | "top" | "right" | "bottom">;

/**
 * A highlight that moves does not slide across the page — it fades out where it was
 * and fades back in where it now belongs. A nudge of a few pixels (a panel settling)
 * is not a move and is applied straight away.
 */
const NUDGE_PX = 24;
function useFadedRect(rect: DOMRect | null) {
  const [shown, setShown] = useState<DOMRect | null>(rect);
  const [visible, setVisible] = useState(!!rect);
  const shownRef = useRef<DOMRect | null>(rect);
  useEffect(() => {
    const current = shownRef.current;
    if (!rect) {
      shownRef.current = null;
      setShown(null);
      setVisible(false);
      return;
    }
    const nudge =
      current &&
      Math.abs(current.top - rect.top) < NUDGE_PX &&
      Math.abs(current.left - rect.left) < NUDGE_PX;
    if (!current || nudge) {
      shownRef.current = rect;
      setShown(rect);
      setVisible(true);
      return;
    }
    setVisible(false);
    const timer = window.setTimeout(() => {
      shownRef.current = rect;
      setShown(rect);
      setVisible(true);
    }, 160);
    return () => window.clearTimeout(timer);
  }, [rect]);
  return { shown, visible };
}

/** Where a callout's numbered chip sits: hanging off the top-left corner of what it marks. */
const CHIP_SIZE = 24;
function chipRect(r: DOMRect): Edges {
  const left = r.left - 14;
  const top = r.top - 14;
  return { left, top, right: left + CHIP_SIZE, bottom: top + CHIP_SIZE };
}

/**
 * A step that numbers things across the page must not sit on top of them. Instead of
 * hanging off its target, the card docks along the viewport's edge. What must stay
 * visible are the numbers and the beacon: a spot that covers any of those loses
 * outright, and only among the spots that cover none does the least-covered region
 * area decide — bottom-right first, since that is where a page is usually quietest.
 */
function dockPosition(
  tooltipWidth: number,
  tooltipHeight: number,
  keepClear: Edges[],
  regions: Edges[],
): Omit<TooltipPosition, "instant"> {
  const padding = 16;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const bottom = viewportHeight - tooltipHeight - padding;
  const right = viewportWidth - tooltipWidth - padding;
  const middle = (viewportWidth - tooltipWidth) / 2;
  const spots = [
    { top: bottom, left: right },
    { top: bottom, left: padding },
    { top: bottom, left: middle },
    { top: padding, left: right },
    { top: padding, left: padding },
    { top: padding, left: middle },
  ];
  const overlap = (c: { top: number; left: number }, r: Edges) => {
    const w = Math.min(c.left + tooltipWidth, r.right) - Math.max(c.left, r.left);
    const h = Math.min(c.top + tooltipHeight, r.bottom) - Math.max(c.top, r.top);
    return w > 0 && h > 0 ? w * h : 0;
  };
  const score = (c: { top: number; left: number }) => {
    const hidden = keepClear.filter((r) => overlap(c, r) > 0).length;
    const area = regions.reduce((sum, r) => sum + overlap(c, r), 0);
    // A hidden number costs more than any amount of covered panel.
    return hidden * viewportWidth * viewportHeight + area;
  };
  let best = spots[0];
  let bestScore = score(best);
  for (const spot of spots.slice(1)) {
    const s = score(spot);
    if (s < bestScore) {
      best = spot;
      bestScore = s;
    }
  }
  return {
    top: Math.max(padding, best.top),
    left: Math.max(padding, best.left),
    arrowPosition: "none",
  };
}

function Beacon({ rect, hidden }: { rect: DOMRect | null; hidden: boolean }) {
  const { shown, visible } = useFadedRect(rect);
  if (!shown) return null;
  return (
    <div
      data-testid="tour-beacon"
      className={cn(
        "pointer-events-none fixed z-[9998] rounded-lg ring-2 ring-primary transition-opacity duration-150 ease-out",
        !visible && "opacity-0",
        // The room lights come back up while the card flies to the corner.
        hidden && "opacity-0 duration-[450ms]",
      )}
      style={{
        top: shown.top - 4,
        left: shown.left - 4,
        width: shown.width + 8,
        height: shown.height + 8,
        boxShadow:
          "0 0 0 6px hsl(var(--primary) / 0.15), 0 0 28px 4px hsl(var(--primary) / 0.35)",
      }}
    >
      <span className="absolute -right-2 -top-2 flex h-4 w-4">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-4 w-4 rounded-full bg-primary ring-2 ring-white" />
      </span>
    </div>
  );
}

function CalloutOutline({
  number,
  rect,
  lit,
  dimmed,
  hidden,
}: {
  number: number;
  rect: DOMRect | null;
  lit: boolean;
  dimmed: boolean;
  hidden: boolean;
}) {
  const { shown, visible } = useFadedRect(rect);
  if (!shown) return null;
  return (
    <div
      data-testid="tour-callout"
      data-lit={lit || undefined}
      className={cn(
        "pointer-events-none fixed rounded-xl transition-opacity duration-150 ease-out",
        // The one under the pointer: a solid ring, a wash and a glow strong enough
        // to read on the dark header as well as a white panel — and above the card,
        // so a control the card happens to sit on still shows through.
        lit
          ? "z-[10002] border-2 border-primary bg-primary/15 shadow-[0_0_0_4px_hsl(var(--primary)/0.3),0_0_36px_6px_hsl(var(--primary)/0.55)]"
          : "z-[9998] border border-dashed border-primary/50",
        dimmed && "opacity-40",
        (!visible || hidden) && "opacity-0",
        hidden && "duration-[450ms]",
      )}
      style={{
        top: shown.top - 2,
        left: shown.left - 2,
        width: shown.width + 4,
        height: shown.height + 4,
      }}
    >
      <span
        className={cn(
          "absolute -left-3 -top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-white shadow-md ring-2 ring-white transition-transform",
          lit && "scale-125",
        )}
      >
        {number}
      </span>
    </div>
  );
}

export function FeatureTour({
  tourKey,
  steps,
  onComplete,
  onSkip,
  isOpen: controlledIsOpen,
  onOpenChange,
  autoStart = true,
  onStepId,
  collapsible = false,
}: FeatureTourProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  // The card visibly travels to the corner so the reader sees where it went;
  // the corner button then glows for a moment to finish the hand-off.
  const [collapseFlight, setCollapseFlight] = useState(false);
  const [cornerGlow, setCornerGlow] = useState(false);
  const collapseTimersRef = useRef<number[]>([]);
  useEffect(
    () => () => {
      collapseTimersRef.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>(null);
  // One box per callout, in the step's order; null for one not on the page right now.
  const [calloutRects, setCalloutRects] = useState<(DOMRect | null)[]>([]);
  const lastCalloutsRef = useRef<(DOMRect | null)[]>([]);
  const calloutElementsRef = useRef<(Element | null)[]>([]);
  // The numbered line the pointer is on: its outline lights up on the page, and the
  // page scrolls to it in case it is out of view.
  const [hoveredCallout, setHoveredCallout] = useState<number | null>(null);
  const [mediaFailed, setMediaFailed] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  // What the spotlight was last drawn from; a tick that reads the same numbers changes nothing.
  const lastMeasureRef = useRef<{
    target: DOMRect;
    tooltipWidth: number;
    tooltipHeight: number;
  } | null>(null);

  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = onOpenChange ?? setInternalIsOpen;
  // A tour that spans pages survives a reload: where it was, and whether it was set aside.
  const stateKey = `tour-state-${tourKey}`;

  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  // The stops are the steps that point at something; an opening dialog is not one.
  const stops = steps.filter((s) => !s.modal);
  const stopNumber = step ? stops.indexOf(step) + 1 : 0;

  // Open on first visit — once no other tour is on screen.
  useEffect(() => {
    if (!autoStart) return;
    if (safeLocalStorage.getItem(`tour-completed-${tourKey}`)) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const anotherTourIsUp = () => !!activeTourKey && activeTourKey !== tourKey;
    const tryOpen = () => {
      if (anotherTourIsUp()) return;
      window.removeEventListener(TOUR_CLOSED_EVENT, tryOpen);
      // Small delay to ensure DOM is ready
      timer = setTimeout(() => {
        // Two tours mounting together both reach this point: the first one claims
        // the screen here, the second sees the claim and goes back to waiting.
        if (anotherTourIsUp()) {
          window.addEventListener(TOUR_CLOSED_EVENT, tryOpen);
          return;
        }
        if (controlledIsOpen === undefined) activeTourKey = tourKey;
        if (collapsible) {
          try {
            const saved = JSON.parse(
              safeLocalStorage.getItem(stateKey) || "null",
            ) as {
              step?: number;
              collapsed?: boolean;
            } | null;
            if (
              saved &&
              typeof saved.step === "number" &&
              saved.step > 0 &&
              saved.step < steps.length
            ) {
              setCurrentStep(saved.step);
              setCollapsed(!!saved.collapsed);
            }
          } catch {
            // Unreadable — start from the top.
          }
        }
        setIsOpen(true);
      }, 500);
    };
    window.addEventListener(TOUR_CLOSED_EVENT, tryOpen);
    tryOpen();
    return () => {
      window.removeEventListener(TOUR_CLOSED_EVENT, tryOpen);
      if (timer) clearTimeout(timer);
    };
  }, [
    tourKey,
    autoStart,
    setIsOpen,
    controlledIsOpen,
    collapsible,
    stateKey,
    steps.length,
  ]);

  useEffect(() => {
    if (!collapsible || !isOpen) return;
    safeLocalStorage.setItem(
      stateKey,
      JSON.stringify({ step: currentStep, collapsed }),
    );
  }, [collapsible, isOpen, currentStep, collapsed, stateKey]);

  // Hold the screen while open (set aside counts as open); hand it back on close or unmount.
  useEffect(() => {
    if (!isOpen) return;
    activeTourKey = tourKey;
    return () => {
      if (activeTourKey === tourKey) {
        activeTourKey = null;
        window.dispatchEvent(new Event(TOUR_CLOSED_EVENT));
      }
    };
  }, [isOpen, tourKey]);

  useEffect(() => {
    onStepId?.(isOpen && !collapsed ? (step?.id ?? null) : null);
  }, [isOpen, collapsed, step, onStepId]);

  // Follow the target for as long as the step is showing. The page a step opens keeps
  // laying itself out well after the step changes — data arrives, images load, panels
  // expand — and a spotlight measured once ends up pointing at where the element used
  // to be. A short interval re-reads the target and touches state only when something
  // moved; scroll and resize are answered right away on top of that. A layout effect so
  // the step's first placement is on screen before the new step paints.
  useLayoutEffect(() => {
    if (!isOpen || collapsed || !step) return;
    // A modal step points at nothing: no spotlight to place, nothing to follow.
    setHoveredCallout(null);
    if (step.modal || !step.target) {
      lastMeasureRef.current = null;
      lastCalloutsRef.current = [];
      calloutElementsRef.current = [];
      setTargetRect(null);
      setTooltipPosition(null);
      setCalloutRects([]);
      return;
    }
    const stepTarget = step.target;
    const callouts = step.callouts ?? [];

    // A new step starts from nowhere: no ring on the old element, popover out of sight
    // until the new target is found, then placed straight onto it.
    lastMeasureRef.current = null;
    lastCalloutsRef.current = [];
    calloutElementsRef.current = [];
    setTargetRect(null);
    setTooltipPosition(null);
    setCalloutRects([]);

    // The numbered chips ride along with whatever they are pinned to, on the same
    // tick as the spotlight; a tick that reads the same boxes changes nothing.
    const followCallouts = () => {
      if (callouts.length === 0) return;
      const found = callouts.map((c) => (c.target ? findTarget(c.target) : null));
      calloutElementsRef.current = found.map((f) => f?.element ?? null);
      const next = found.map((f) => f?.rect ?? null);
      const last = lastCalloutsRef.current;
      const same =
        last.length === next.length &&
        next.every((rect, i) => {
          const prev = last[i];
          if (!rect || !prev) return rect === prev;
          return sameBox(prev, rect);
        });
      if (same) return;
      lastCalloutsRef.current = next;
      setCalloutRects(next);
      // The card docks clear of the callouts, so when they move it re-places itself.
      lastMeasureRef.current = null;
    };
    // One scroll to bring the target into view; after that the reader's own scrolling wins.
    let scrolled = false;
    // A step's page is still settling for the first moment — a panel opens, a list
    // arrives. A step that would rather point at a button on that page than at the
    // menu entry that opened it waits out that moment for the button, instead of
    // landing on the menu and then hopping across the screen. After it, the step
    // stays on whatever it settled on; a menu opened later must not drag it away.
    // Counted in ticks of the follow interval rather than wall-clock time, so it
    // behaves the same under test as in a browser. Long enough for a Settings tab
    // to fetch and lay out its panel — a step must land on the import panel once, not
    // on the menu first and the panel a second later.
    const SETTLE_TICKS = 30; // ≈ 2.4 s at 80 ms a tick
    let ticks = 0;
    let pinned: Element | null = null;

    const follow = () => {
      followCallouts();
      const settled = ticks > SETTLE_TICKS;
      if (pinned && settled) {
        if (!pinned.isConnected) {
          pinned = null;
        } else {
          const pinnedRect = pinned.getBoundingClientRect();
          if (pinnedRect.width === 0 || pinnedRect.height === 0) pinned = null;
        }
      } else {
        pinned = null;
      }
      let found = pinned
        ? { element: pinned, rect: pinned.getBoundingClientRect(), rank: 0 }
        : findTarget(stepTarget);
      if (found && found.rank > 0 && !settled) found = null;
      if (found) pinned = found.element;
      if (!found) {
        if (lastMeasureRef.current) {
          lastMeasureRef.current = null;
          setTargetRect(null);
          setTooltipPosition(null);
        }
        return;
      }

      let { rect } = found;
      const onScreen =
        rect.top >= 8 && rect.bottom <= window.innerHeight - 8;
      if (!onScreen && !scrolled) {
        scrolled = true;
        found.element.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "auto",
        });
        rect = found.element.getBoundingClientRect();
      }

      // The popover's own size matters as much as the target's: a QR code or a font
      // arriving late changes where "centred under the button" is.
      const tooltipWidth = tooltipRef.current?.offsetWidth ?? 0;
      const tooltipHeight = tooltipRef.current?.offsetHeight ?? 0;
      const last = lastMeasureRef.current;
      if (
        last &&
        sameBox(last.target, rect) &&
        last.tooltipWidth === tooltipWidth &&
        last.tooltipHeight === tooltipHeight
      ) {
        return;
      }
      lastMeasureRef.current = { target: rect, tooltipWidth, tooltipHeight };

      setTargetRect(rect);
      const calloutRectsNow = lastCalloutsRef.current.filter(
        (r): r is DOMRect => !!r,
      );
      const position =
        callouts.length > 0
          ? dockPosition(
              tooltipWidth,
              tooltipHeight,
              [rect, ...calloutRectsNow.map(chipRect)],
              calloutRectsNow,
            )
          : calculatePosition(rect, tooltipWidth, tooltipHeight, step.placement);
      setTooltipPosition((prev) => ({ ...position, instant: prev === null }));
    };

    follow();
    const ticker = setInterval(() => {
      ticks += 1;
      follow();
    }, 80);
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);

    return () => {
      clearInterval(ticker);
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [isOpen, collapsed, step]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const beginCollapse = () => {
    if (collapseFlight) return;
    setCollapseFlight(true);
    collapseTimersRef.current.push(
      window.setTimeout(() => {
        setCollapseFlight(false);
        setCollapsed(true);
        setCornerGlow(true);
        collapseTimersRef.current.push(
          window.setTimeout(() => setCornerGlow(false), 3000),
        );
      }, 450),
    );
  };

  const close = () => {
    safeLocalStorage.removeItem(stateKey);
    safeLocalStorage.setItem(`tour-completed-${tourKey}`, "true");
    setIsOpen(false);
    setCollapsed(false);
    setCurrentStep(0);
  };

  const handleComplete = () => {
    close();
    onComplete?.();
  };

  const handleSkip = () => {
    close();
    onSkip?.();
  };

  // Escape ends the tour the same way Skip does — persisted as completed,
  // overlay gone, page usable. "Try it yourself" / Later still only parks it.
  useEffect(() => {
    if (!isOpen || collapsed) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      handleSkip();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, collapsed]);

  if (!isOpen || !step) return null;

  // The env banner wraps the app in translateZ(0) so sidebar `fixed` stays
  // below it. That same containing block would shift this overlay down by the
  // banner height, because getBoundingClientRect is viewport-relative. Portal
  // to body so `fixed` matches those rects with or without the banner.

  // Set aside: the page is fully usable; the tour waits in the corner as a
  // question mark, its tooltip saying what it is and where it left off.
  if (collapsed) {
    return createPortal(
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid="tour-collapsed"
              onClick={() => setCollapsed(false)}
              aria-label="Resume the tour"
              // pointer-events-auto: a dialog open on the page switches the body off for
              // clicks, and this button lives outside it — without this, resuming would
              // wait until whatever the reader opened is closed again.
              className={cn(
                "pointer-events-auto fixed bottom-4 right-4 z-[10001] flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-white text-primary shadow-2xl transition-transform hover:scale-105 active:scale-[0.98]",
                // Fresh from a collapse: glow for a moment so the eye lands here.
                cornerGlow &&
                  "animate-pulse ring-4 ring-primary/60 shadow-[0_0_24px_6px_rgba(34,197,94,0.45)]",
              )}
            >
              <Question className="h-6 w-6" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">
            Your tour of Ruta is waiting — stop {Math.max(1, stopNumber)} of{" "}
            {stops.length}. Click to pick it back up.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>,
      document.body,
    );
  }

  const callouts = step.callouts ?? [];

  return createPortal(
    <>
      {/* A modal step is a dialog and dims the page behind it. Clicking that page sets
          the tour aside when it allows that, otherwise dismisses. A pointer step dims
          nothing: the page stays readable and usable around the beacon. */}
      {step.modal && (
        <div
          data-testid="tour-scrim"
          className={cn(
            "fixed inset-0 z-[9998] bg-black/60 transition-opacity",
            collapseFlight && "opacity-0 duration-[450ms]",
          )}
          onClick={collapsible ? beginCollapse : handleSkip}
        />
      )}

      {/* Beacon: a forest ring and a pulsing dot on the control the step is about, so the
          eye lands there without the rest of the page going dark. */}
      <Beacon rect={targetRect} hidden={collapseFlight} />

      {/* Callouts: a numbered chip on each thing the step names, with a faint outline
          of what the number covers. */}
      {callouts.map((callout, index) => {
        const lit = hoveredCallout === index;
        // An unpinned callout has no outline of its own; it appears only while lit.
        const rect =
          callout.pin === false && !lit ? null : (calloutRects[index] ?? null);
        return (
          <CalloutOutline
            key={index}
            number={index + 1}
            rect={rect}
            lit={lit}
            dimmed={hoveredCallout !== null && !lit}
            hidden={collapseFlight}
          />
        );
      })}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        data-testid="tour-tooltip"
        className={cn(
          "fixed z-[10001] rounded-xl border border-stone-200 bg-white shadow-2xl",
          // A modal step is an announcement, not a pointer — it gets a dialog's presence,
          // and arrives the way a dialog does.
          step.modal
            ? "w-[34rem] max-w-[calc(100vw-2rem)] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300"
            : "w-[22rem]",
          // A modal step sits centred over the page, like a dialog.
          step.modal && "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
          // A step's first placement lands on the spot; later moves within the step glide.
          !step.modal &&
            tooltipPosition &&
            !tooltipPosition.instant &&
            "transition-all duration-300 ease-out",
          // Nothing to point at yet: stay out of sight, and out of the way of clicks.
          !step.modal && !tooltipPosition && "opacity-0 pointer-events-none",
          // On its way to the corner: the reader watches it shrink into the
          // question mark so they know where to find it.
          collapseFlight && "pointer-events-none",
        )}
        style={
          collapseFlight
            ? {
                top: window.innerHeight - 42,
                left: window.innerWidth - 42,
                transform: "translate(-50%, -50%) scale(0.05)",
                opacity: 0,
                transition: "all 450ms cubic-bezier(0.4, 0, 1, 1)",
              }
            : step.modal
              ? undefined
              : {
                  top: tooltipPosition?.top ?? 0,
                  left: tooltipPosition?.left ?? 0,
                }
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Arrow — a docked card points at nothing and has none. */}
        {tooltipPosition && tooltipPosition.arrowPosition !== "none" && (
          <div
            className={cn(
              "absolute w-3 h-3 bg-white border-stone-200 transform rotate-45",
              tooltipPosition.arrowPosition === "top" &&
                "-top-1.5 left-1/2 -translate-x-1/2 border-l border-t",
              // The bottom edge is the footer, so the arrow there wears the footer's colour.
              tooltipPosition.arrowPosition === "bottom" &&
                "-bottom-1.5 left-1/2 -translate-x-1/2 border-r border-b bg-stone-50",
              tooltipPosition.arrowPosition === "left" &&
                "-left-1.5 top-1/2 -translate-y-1/2 border-l border-b",
              tooltipPosition.arrowPosition === "right" &&
                "-right-1.5 top-1/2 -translate-y-1/2 border-r border-t",
            )}
          />
        )}

        {step.modal ? (
          <>
            <div className="flex items-start justify-between gap-3 border-b border-stone-200 bg-white px-6 py-4">
              <div className="flex min-w-0 items-center gap-3">
                {step.icon && (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 [&>img]:rounded-lg">
                    {step.icon}
                  </div>
                )}
                <h3 className="text-lg font-semibold leading-tight text-stone-900">
                  {step.title}
                </h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="-mr-1.5 -mt-0.5 h-8 shrink-0 px-2 text-stone-500 hover:text-stone-700"
                onClick={handleSkip}
                aria-label="Skip tour"
                data-testid="tour-skip"
              >
                Skip
              </Button>
            </div>
            <div className="bg-stone-50">
              {step.media && !mediaFailed && (
                <video
                  data-testid="tour-media"
                  src={step.media.src}
                  poster={step.media.poster}
                  controls
                  playsInline
                  preload="metadata"
                  className="aspect-video w-full bg-nav object-cover"
                  onError={() => setMediaFailed(true)}
                />
              )}
              <p className="px-6 py-4 text-sm leading-relaxed text-muted-foreground">
                {step.content}
              </p>
              {step.render && (
                <div className="border-t border-stone-100 px-6 pb-5">{step.render}</div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Header: the stop's icon on a tinted tile, where it sits in the tour, its name. */}
            <div className="flex items-start justify-between gap-3 px-4 pt-4">
              <div className="flex min-w-0 items-center gap-3">
                {step.icon && (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&>svg]:h-[18px] [&>svg]:w-[18px]">
                    {step.icon}
                  </span>
                )}
                <div className="min-w-0">
                  {stops.length > 1 && stopNumber > 0 && (
                    <div
                      className="text-[11px] font-medium text-stone-400"
                      data-testid="tour-stop-counter"
                    >
                      {stopNumber} of {stops.length}
                    </div>
                  )}
                  <h3 className="text-[15px] font-semibold leading-tight text-stone-900">
                    {step.title}
                  </h3>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="-mr-1.5 -mt-1.5 h-8 shrink-0 px-2 text-xs text-stone-500 hover:text-stone-700"
                onClick={handleSkip}
                aria-label="Skip tour"
                data-testid="tour-skip"
              >
                Skip
              </Button>
            </div>

            {/* Content: one line on what it is, one on what to press. */}
            <div className="space-y-2.5 px-4 pb-4 pt-3">
              <p className="text-sm leading-snug text-stone-600">
                {step.content}
              </p>
              {step.action && (
                <p className="flex items-start gap-2 text-sm font-medium leading-snug text-stone-900">
                  <ArrowRight
                    weight="bold"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
                  />
                  <span>{step.action}</span>
                </p>
              )}
              {callouts.length > 0 && (
                <ol className="-mx-1.5 space-y-0.5" data-testid="tour-callout-list">
                  {callouts.map((callout, index) => (
                    <li
                      key={index}
                      className={cn(
                        "flex cursor-pointer select-none items-start gap-2 rounded-md px-1.5 py-1 text-sm text-stone-700 transition-colors",
                        hoveredCallout === index && "bg-primary/[0.06] text-stone-900",
                      )}
                      // Hovering a line finds its section on the page — scrolled into
                      // view if it is off-screen, lit up either way.
                      onMouseEnter={() => {
                        setHoveredCallout(index);
                        calloutElementsRef.current[index]?.scrollIntoView({
                          block: "nearest",
                          inline: "nearest",
                          behavior: "smooth",
                        });
                      }}
                      onMouseLeave={() =>
                        setHoveredCallout((current) =>
                          current === index ? null : current,
                        )
                      }
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white">
                        {index + 1}
                      </span>
                      <span className="leading-snug">{callout.label}</span>
                    </li>
                  ))}
                </ol>
              )}
              {step.render}
            </div>
          </>
        )}

        {/* Footer */}
        {step.modal ? (
          // A modal step announces the tour: Start begins it, Later parks it in
          // the corner (or ends it when it cannot wait).
          <div className="flex items-center justify-end gap-2 rounded-b-xl border-t border-stone-100 bg-white px-6 py-4">
            <Button
              variant="ghost"
              onClick={collapsible ? beginCollapse : handleSkip}
              className="h-10 px-4"
            >
              Later
            </Button>
            <Button onClick={handleNext} className="h-10 px-5">
              Start the tour
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="relative flex items-center justify-between gap-2 rounded-b-xl border-t border-stone-100 bg-stone-50 px-3 py-2.5">
            {/* How far along the tour is, as a hairline across the top of the footer. */}
            {stops.length > 1 && (
              <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-stone-200/70">
                <div
                  className="h-full bg-primary transition-[width] duration-300"
                  style={{ width: `${(stopNumber / stops.length) * 100}%` }}
                />
              </div>
            )}
            {collapsible ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={beginCollapse}
                className="h-8 px-2 text-xs text-stone-500"
                title="Use this page for a bit; the tour waits in the corner"
              >
                <ArrowsInSimple className="mr-1 h-3.5 w-3.5" />
                Try it yourself
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-1">
              {!isFirstStep && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrevious}
                  className="h-8 px-2 text-xs"
                >
                  <CaretLeft className="mr-1 h-3.5 w-3.5" />
                  Back
                </Button>
              )}
              <Button size="sm" onClick={handleNext} className="h-8 px-3 text-xs">
                {isLastStep ? "Done" : "Next"}
                {!isLastStep && <CaretRight className="ml-1 h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

// Hook to manually trigger a tour
export function useFeatureTour(tourKey: string) {
  const [isOpen, setIsOpen] = useState(false);

  const startTour = useCallback(() => {
    setIsOpen(true);
  }, []);

  const resetTour = useCallback(() => {
    safeLocalStorage.removeItem(`tour-completed-${tourKey}`);
  }, [tourKey]);

  const hasCompletedTour = useCallback(() => {
    return safeLocalStorage.getItem(`tour-completed-${tourKey}`) === "true";
  }, [tourKey]);

  return {
    isOpen,
    setIsOpen,
    startTour,
    resetTour,
    hasCompletedTour,
  };
}
